/* eslint-disable no-restricted-syntax */

/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { createContext, useEffect, useRef, useState } from 'react';

import moment from 'moment';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { Button, Grid, LoadingSection, Typography2 } from '@coursera/cds-core';

import { PRODUCT_TYPE_NAMES } from 'bundles/browse/constants';
import { Drawer } from 'bundles/enterprise-admin-drawers/components/Drawer';
import type { Props as PropsFromCaller } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawer';
import type { PrivateSessionEditDrawerEnterpriseProductConfigurationMetadataQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_specializationMember_specialization as S12nMetaData } from 'bundles/enterprise-admin-drawers/components/__generated__/PrivateSessionEditDrawerEnterpriseProductConfigurationMetadataQuery';
import type { PrivateSessionEditDrawerQuery_EnterpriseProductConfigurationMetadataV1Resource_get_metadataType_EnterpriseProductConfigurationMetadataV1_courseMember_course as CourseMetaData } from 'bundles/enterprise-admin-drawers/components/__generated__/PrivateSessionEditDrawerQuery';
import { DrawerWarning } from 'bundles/enterprise-admin-drawers/components/components/DrawerInfoBoxes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import PrivateSessionEditDrawerStep1 from 'bundles/enterprise-admin-drawers/components/components/PrivateSessionEditDrawerStep1';
import PrivateSessionEditDrawerStep2 from 'bundles/enterprise-admin-drawers/components/components/PrivateSessionEditDrawerStep2';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import PrivateSessionEditDrawerStep3 from 'bundles/enterprise-admin-drawers/components/components/PrivateSessionEditDrawerStep3';
import type {
  CourseSession,
  CustomSessionOptionType,
  SessionOptionType,
} from 'bundles/enterprise-admin-drawers/components/types';
import type { PropsFromUsePrivateSessionEditDrawerData } from 'bundles/enterprise-admin-drawers/components/usePrivateSessionEditDrawerData';
import type { PropsFromUseSessionInfo } from 'bundles/enterprise-admin-drawers/components/useSessionInfo';
import { useProgramAdminTypeAndPermissionsContext } from 'bundles/enterprise-admin-program-catalog/components/ProgramAdminTypeAndPermissionsContextProvider';
import type { DataForProductConfigurationMutationWithType } from 'bundles/enterprise-admin-sessions/utils/enterpriseProductConfigurationOperationsUpdate';
import { enterpriseProductConfigurationOperationsBulkUpdate } from 'bundles/enterprise-admin-sessions/utils/enterpriseProductConfigurationOperationsUpdate';
import {
  assertIsExternallyManagedOption,
  assertIsSessionOptionWithSessionId,
} from 'bundles/enterprise-admin-sessions/utils/typeHelpers';
import { Heading, Spacer, TrackedButton } from 'bundles/enterprise-ui/components';
import {
  CreditBadge,
  ExclusiveBadge,
  ItemLockedBadge,
  ProductTypeBadge,
  RecommendedForCreditBadge,
} from 'bundles/enterprise-ui/components/Badges';
import type { DateRangeValue } from 'bundles/enterprise-ui/components/DateRangeInput';
import { useCampusWordingForAdmin } from 'bundles/enterprise-ui/useCampusWordingForAdmin';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import type {
  EligibleForExternallyManagedSession,
  EnterpriseAdminSessionSelectionOptionType,
  EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption,
  EnterpriseAdminSessionSelectionOptionTypeFlexibleOption,
  EnterpriseExternallyManagedSessionSelectionOption,
  EnterpriseProductConfigurationTypeCourse,
  EnterpriseProductConfigurationTypeSpecialization,
} from 'bundles/naptimejs/resources/__generated__/EnterpriseProductConfigurationsV1';

import _t from 'i18n!nls/admin-dashboard';

const LOGO_SIZE = 80;

const drawerContainerStyle = css`
  display: flex;
  flex-direction: column;
  padding: 36px;
  height: 100%;
  overflow: scroll;
  /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
  font-family: 'Open Sans';

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  h2 {
    font-size: 34px;
    line-height: 40px;
    margin-bottom: 36px;
    color: #000;
  }

  h4 {
    font-size: 16px;
  }

  strong {
    color: #1f1f1f;
  }
`;

const productDetailsStyle = css`
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 38px;
  margin-bottom: 16px;

  img {
    margin-right: 18px;
    border-radius: 4px;
  }
`;

const navButtonGroupStyle = css`
  display: flex;
  margin-top: auto;
`;

export type Props = PropsFromCaller &
  PropsFromUsePrivateSessionEditDrawerData & { isLoading?: boolean } & PropsFromUseSessionInfo;

type EnterpriseAdminProductSessionSelectionOption = {
  isSelected: boolean;
  optionType: EnterpriseAdminSessionSelectionOptionType;
  courseId?: string;
};

type PrivateSessionDrawerContextType = {
  isS12n?: boolean;
  isSelectionDisabled?: boolean;
  isProfessionalCertificate?: boolean;
};

export const PrivateSessionDrawerContext = createContext<PrivateSessionDrawerContextType>({
  isS12n: false,
  isSelectionDisabled: false,
  isProfessionalCertificate: false,
});

export const getDateErrorMessage = (minNumberOfDays: number, isS12n?: boolean, isProfessionalCertificate?: boolean) => {
  if (isS12n) {
    return isProfessionalCertificate ? (
      <FormattedMessage
        message={_t(
          'Update the limited availability to match the duration of this Professional Certificate: {duration}'
        )}
        duration={<strong>{Math.round(minNumberOfDays / 7).toString()} weeks</strong>}
        data-testid="cert-limited-availability-error"
      />
    ) : (
      <FormattedMessage
        message={_t('Update the limited availability to match the duration of this Specialization: {duration}')}
        duration={<strong>{Math.round(minNumberOfDays / 7).toString()} weeks</strong>}
        data-testid="s12n-limited-availability-error"
      />
    );
  } else {
    return (
      <FormattedMessage
        message={_t('Update the limited availability to match the duration of this course: {duration}')}
        duration={<strong>{Math.round(minNumberOfDays / 7).toString()} weeks</strong>}
      />
    );
  }
};

const PrivateSessionEditDrawerContent = ({
  programId,
  productId,
  isOpen,
  productType,
  isIncludedInContentCuration,
  naptime,
  override,
  onRequestClose,
  onSavedSessionMetadata,
  onSelectSession,
  useAsDialogContent,
  onLoadingStateChange,
  onHandleDialogRedirect,
  handleDialogFlowComplete,
  submitting,
  onSubmittingStateChange,
  onHasErrorStateChange,
  currentSessionOption,
  onCurrentSessionOptionChange,
  metaData,
  course,
  s12n,
  lenOfS12n,
  isProfessionalCertificate,
  productConfig,
  sessions = [],
  isLoading,
}: Props) => {
  const [selectableSessions, setSelectableSessions] = useState<Array<EnterpriseAdminProductSessionSelectionOption>>([]);
  const [selectedSessionOption, setSelectedSessionOption] = useState<SessionOptionType | undefined>(undefined);
  const [selectedSession, setSelectedSession] = useState<CourseSession | undefined>({});
  const [originalSession, setOriginalSession] = useState<CourseSession | undefined>({});
  const [migrateLearners, setMigrateLearners] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | React.ReactNode | undefined>();
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const isS12n = productType === PRODUCT_TYPE_NAMES.SPECIALIZATION;
  const { orgId, userId } = useProgramAdminTypeAndPermissionsContext();
  const useCampusWording = useCampusWordingForAdmin();

  const [shouldOfferProductForCredit, setShouldOfferProductForCredit] = useState<boolean | undefined>(
    metaData?.isSelectedForCredit
  );
  // Add new state to track the checkbox to require all content completion
  const [shouldRequireItemCompletion, setShouldRequireItemCompletion] = useState<boolean | undefined>(
    (metaData as CourseMetaData)?.isSelectedToRequireAllItemCompletion
  );
  const [shouldAutoEnroll, setShouldAutoEnroll] = useState<boolean | undefined>(metaData?.isAutoEnrollmentEnabled);
  const [isMutationInProgress, setIsMutationInProgress] = useState(false);
  const hasDialogFlowCompleted = useRef(false);

  const getCustomSessionOption = (): CustomSessionOptionType => {
    return {
      typeName: 'createExternallyManagedSelection',
      definition: {
        sessionId: '',
      },
    };
  };

  const getFlexibleSessionOption = (isSelected = false): EnterpriseAdminProductSessionSelectionOption => {
    return {
      isSelected,
      optionType: {
        definition: { versionName: '' },
        typeName: 'flexibleOption' as const,
      } as EnterpriseAdminSessionSelectionOptionTypeFlexibleOption,
    };
  };

  const setUpSelectableSessions = () => {
    if (!isS12n) {
      return (
        (productConfig?.productConfigurationType as EnterpriseProductConfigurationTypeCourse)?.definition
          ?.selectableSessions || []
      );
    } else if ((metaData as S12nMetaData)?.associatedSessionId) {
      // A configured S12n
      const createSessionEligibility = productConfig?.productConfigurationType?.definition?.createSessionEligibility
        .definition as EligibleForExternallyManagedSession;
      const externallyManagedOptionType: EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption = {
        typeName: 'externallyManagedSessionOption',
        definition: {
          canEdit: Boolean(productConfig?.productConfigurationType?.definition?.canEditConfiguration),
          // It will only enter the else if block if associatedSessionId is defined
          sessionId: (metaData as S12nMetaData).associatedSessionId!,
          maxEndDate: createSessionEligibility?.maxEndDate ?? 0,
          minNumberOfDays: createSessionEligibility?.minNumberOfDays ?? 0,
          name: '',
        },
      };
      return [
        getFlexibleSessionOption(),
        {
          isSelected: true,
          optionType: externallyManagedOptionType,
        },
      ];
    } else {
      // Either an unconfigured s12n or a s12n with a flexible schedule
      return [getFlexibleSessionOption(true)];
    }
  };

  useEffect(() => {
    onLoadingStateChange?.(Boolean(isLoading));
  }, [isLoading, onLoadingStateChange]);

  useEffect(() => {
    onHasErrorStateChange?.(Boolean(dateError));
  }, [dateError, onHasErrorStateChange]);

  useEffect(() => {
    onCurrentSessionOptionChange?.(selectedSessionOption);
  }, [selectedSessionOption, onCurrentSessionOptionChange]);

  useEffect(
    () => {
      if (!productConfig?.id || Boolean(currentSessionOption)) {
        return;
      }

      const tempSelectableSessions: Array<EnterpriseAdminProductSessionSelectionOption> = setUpSelectableSessions();
      setSelectableSessions(tempSelectableSessions);

      if (override) {
        if (override.id) {
          const selectedOption = tempSelectableSessions.find(
            (session: EnterpriseAdminProductSessionSelectionOption) => {
              if (assertIsSessionOptionWithSessionId(session.optionType)) {
                return session.optionType.definition.sessionId === override.id;
              } else {
                return false;
              }
            }
          )?.optionType;
          setSelectedSessionOption(selectedOption);
        } else if (override.startsAt) {
          setSelectedSessionOption(getCustomSessionOption());
        } else {
          setSelectedSessionOption(
            tempSelectableSessions.find((session) => session.optionType.typeName === 'flexibleOption')?.optionType
          );
        }
        setSelectedSession(override as CourseSession);
        setShouldOfferProductForCredit(override?.isSelectedForCredit);
        setShouldRequireItemCompletion(override?.isSelectedToRequireAllItemCompletion);
        setShouldAutoEnroll(override?.isAutoEnrollmentEnabled);
        return;
      }

      const selectedOption = tempSelectableSessions.find((session) => session.isSelected)?.optionType;
      setSelectedSessionOption(selectedOption);

      if (assertIsSessionOptionWithSessionId(selectedOption)) {
        const onDemandSession = {
          ...((sessions.find((s) => s.id === selectedOption.definition.sessionId) as CourseSession) ?? {}),
        };
        if (Object.keys(onDemandSession).length === 0) return;

        if (selectedOption?.typeName === 'privateSessionGroupOption') {
          onDemandSession.name = selectedOption.definition.groupName;
        } else {
          onDemandSession.name = selectedOption.definition.name;
        }

        setSelectedSession(onDemandSession as CourseSession);
        setOriginalSession(onDemandSession as CourseSession);
      } else {
        setSelectedSession({ courseId: productId } as CourseSession);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, override, productId, productConfig?.id, currentSessionOption]
  );

  const getBaseCustomSession = (): CourseSession => {
    return {
      startsAt: momentWithUserTimezone().add(1, 'd').startOf('day').unix() * 1000,
      endsAt: momentWithUserTimezone().add(1, 'd').endOf('day').add(3, 'M').unix() * 1000,
      courseId: productId,
    };
  };

  const hasUpdatedAvailability = (): boolean => {
    if (!selectedSession) {
      return false;
    }

    const originalOption = selectableSessions.find((option) => option.isSelected)?.optionType;

    if (
      assertIsSessionOptionWithSessionId(selectedSessionOption) &&
      assertIsSessionOptionWithSessionId(originalOption) &&
      selectedSessionOption.definition.sessionId !== originalOption?.definition.sessionId
    ) {
      return true;
    }

    return (
      selectedSession?.endsAt !== originalSession?.endsAt || selectedSession?.startsAt !== originalSession?.startsAt
    );
  };

  const hasUpdatedName = (): boolean => {
    return selectedSession?.name !== originalSession?.name && selectedSessionOption?.typeName !== 'flexibleOption';
  };

  const hasNewSession = (): boolean => {
    const originalOption = selectableSessions.find((option) => option.isSelected)?.optionType;

    if (!originalOption || !selectedSessionOption) {
      return false;
    }
    if (
      assertIsSessionOptionWithSessionId(selectedSessionOption) &&
      assertIsSessionOptionWithSessionId(originalOption)
    ) {
      return (
        selectedSessionOption?.typeName !== originalOption?.typeName ||
        selectedSessionOption.definition.sessionId !== originalOption?.definition.sessionId
      );
    } else {
      return selectedSessionOption?.typeName !== originalOption?.typeName;
    }
  };

  const hasUpdatedCreditStatus = (): boolean => {
    return !!metaData?.isSelectedForCredit !== shouldOfferProductForCredit;
  };

  const hasUpdatedItemCompletionStatus = (): boolean => {
    return !!(metaData as CourseMetaData)?.isSelectedToRequireAllItemCompletion !== shouldRequireItemCompletion;
  };

  const hasUpdatedAutoEnroll = (): boolean => {
    return !!metaData?.isAutoEnrollmentEnabled !== shouldAutoEnroll;
  };

  const generateS12nCourseBulkUpdateData = () => {
    if (!s12n || !selectedSessionOption || !productId) return;

    const activeSessionsForCoursesInSpecialization = (
      productConfig?.productConfigurationType as EnterpriseProductConfigurationTypeSpecialization
    ).definition.activeSessionsForCoursesInSpecialization;

    const updates: DataForProductConfigurationMutationWithType[] = s12n.latestSpecialization.courses.map((course) => {
      // If we're editing an existingExternallyManagedSession we need to pass in the unique course sessionId, not the s12n sessionId
      const activeSession = activeSessionsForCoursesInSpecialization.find(
        (session) => session.courseId === course.id
      )?.optionType;

      return {
        productId: course.id,
        session: {
          ...selectedSession,
          ...(selectedSessionOption.typeName === 'externallyManagedSessionOption'
            ? { id: (activeSession?.definition as EnterpriseExternallyManagedSessionSelectionOption)?.sessionId }
            : {}),
        },
        option: { ...selectedSessionOption },
        migrateLearners,
        isSelectedForCredit: shouldOfferProductForCredit,
        isSelectedToRequireAllItemCompletion: shouldRequireItemCompletion,
        shouldAutoEnroll,
        parentS12nId: s12n.id,
        isS12n: false,
      };
    });

    // Add the s12n update data
    updates?.push({
      productId,
      isSelectedForCredit: shouldOfferProductForCredit,
      isSelectedToRequireAllItemCompletion: shouldRequireItemCompletion,
      shouldAutoEnroll,
      isS12n: true,
      session: selectedSession || {},
      option: selectedSessionOption,
      migrateLearners,
    });

    return updates;
  };

  useEffect(() => {
    // This checks for whether a mutation for async update on the configuration is still in progress or not
    // without this check, when adding custom availability, the mutation will get called twice causing the same custom availability to be duplicated
    if (isMutationInProgress || hasDialogFlowCompleted.current) {
      return;
    }

    if (
      onHandleDialogRedirect &&
      handleDialogFlowComplete &&
      programId &&
      productId &&
      selectedSessionOption &&
      onSubmittingStateChange
    ) {
      onSubmittingStateChange(true);
      setIsMutationInProgress(true);

      const mutation = enterpriseProductConfigurationOperationsBulkUpdate(programId, [
        {
          productId,
          session: selectedSession || {},
          option: selectedSessionOption,
          migrateLearners,
          isSelectedForCredit: shouldOfferProductForCredit,
          isSelectedToRequireAllItemCompletion: shouldRequireItemCompletion,
          shouldAutoEnroll,
          isS12n: false,
        },
      ]);
      naptime
        .executeMutation(mutation)
        .then(() => {
          hasDialogFlowCompleted.current = true;
          onHandleDialogRedirect();
        })
        .finally(() => {
          // Reset the submission state
          setIsMutationInProgress(false);
        });
    }
  }, [
    handleDialogFlowComplete,
    migrateLearners,
    naptime,
    onHandleDialogRedirect,
    productId,
    programId,
    selectedSession,
    selectedSessionOption,
    shouldAutoEnroll,
    shouldOfferProductForCredit,
    shouldRequireItemCompletion,
    onSubmittingStateChange,
    isMutationInProgress,
  ]);

  if (submitting) {
    return <LoadingSection />;
  }

  const handleSave = async () => {
    if (
      (selectedSessionOption?.typeName !== `flexibleOption` && (!selectedSession || !selectedSessionOption)) ||
      !programId ||
      !productId
    ) {
      return;
    }

    if (onSelectSession && selectedSession && productId) {
      onSelectSession({
        productId,
        selectedSession,
        selectedSessionOption,
        isSelectedForCredit: Boolean(shouldOfferProductForCredit),
        isSelectedToRequireAllItemCompletion: Boolean(shouldRequireItemCompletion),
        isAutoEnrollmentEnabled: Boolean(shouldAutoEnroll),
        productType,
      });

      onRequestClose();
      return;
    }

    let mutation;

    if (isS12n) {
      const s12nMutationData = generateS12nCourseBulkUpdateData();
      mutation = s12nMutationData
        ? enterpriseProductConfigurationOperationsBulkUpdate(programId, s12nMutationData)
        : undefined;
    } else {
      mutation = enterpriseProductConfigurationOperationsBulkUpdate(programId, [
        {
          productId,
          session: selectedSession || {},
          option: selectedSessionOption,
          migrateLearners,
          isSelectedForCredit: shouldOfferProductForCredit,
          isSelectedToRequireAllItemCompletion: shouldRequireItemCompletion,
          shouldAutoEnroll,
          isS12n: false,
        },
      ]);
    }
    try {
      setSaving(true);
      if (mutation) {
        await naptime.executeMutation(mutation);
      }

      onRequestClose();

      setTimeout(() => {
        onSavedSessionMetadata?.();
        naptime.refreshData({ resources: ['enterpriseProductConfigurations.v1', 'onDemandCourseSessions.v1'] });
      }, 500);
    } finally {
      setSaving(false);
    }
  };

  const handleNextStep = () => {
    if (step === 0 && ((!hasUpdatedAvailability() && !hasNewSession()) || onSelectSession)) {
      setStep(2);
    } else if (step === 2) {
      handleSave();
    } else {
      setStep((oldStep) => oldStep + 1);
    }
  };

  const handleChangeSession = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [type, id] = e.target.value.split('~');
    const onDemandSession = (
      type === 'flexibleOption' ? { courseId: productId } : sessions.find((s) => s.id === id) || getBaseCustomSession()
    ) as CourseSession | undefined;

    const selected =
      type === 'createExternallyManagedSelection'
        ? getCustomSessionOption()
        : selectableSessions.find((session) => {
            if (assertIsSessionOptionWithSessionId(session.optionType)) {
              if (onDemandSession && session.optionType.typeName === 'externallyManagedSessionOption') {
                onDemandSession.name = session.optionType.definition.name;
              } else if (onDemandSession && session.optionType.typeName === 'privateSessionGroupOption') {
                onDemandSession.name = session.optionType.definition.groupName;
              }
              return session.optionType.typeName === type && session.optionType.definition.sessionId === id;
            } else {
              return session.optionType.typeName === type;
            }
          })?.optionType;

    setSelectedSessionOption(selected);
    setSelectedSession(onDemandSession);
    setDateError(undefined);
  };

  const handleChangeCustomDate = (date: DateRangeValue) => {
    const startsAt = momentWithUserTimezone(date.startDate).startOf('day').unix() * 1000;
    const endsAt = momentWithUserTimezone(date.endDate).endOf('day').unix() * 1000;

    setSelectedSession({
      ...selectedSession,
      startsAt,
      endsAt,
    });

    const createSessionEligibility = productConfig?.productConfigurationType?.definition?.createSessionEligibility;
    let minNumberOfDays = 0;

    if (assertIsExternallyManagedOption(selectedSessionOption)) {
      minNumberOfDays = selectedSessionOption.definition.minNumberOfDays;
    } else if (createSessionEligibility?.typeName === 'eligible') {
      minNumberOfDays = createSessionEligibility?.definition.minNumberOfDays;
    }

    if (minNumberOfDays > 0) {
      const duration = moment(endsAt).diff(moment(startsAt), 'days');
      if (duration < minNumberOfDays) {
        setDateError(getDateErrorMessage(minNumberOfDays, isS12n, isProfessionalCertificate));
      } else {
        setDateError(undefined);
      }
    }
  };

  const handleChangeCustomName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSession({
      ...selectedSession,
      name: e.target.value,
    });
  };

  const handleMigrateLearners = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMigrateLearners(e.target.value === 'true');
  };

  const handleOfferCourseForCredit = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldOfferProductForCredit(e.target.value === 'true');
  };

  const handleToRequireAllContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldRequireItemCompletion(e.target.checked);
  };

  const handleChangeAutoEnroll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldAutoEnroll(e.target.value === 'true');
  };

  const getIsSubmitDisabledWithOverride = (): boolean => {
    return (
      override?.isSelectedForCredit === shouldOfferProductForCredit &&
      override?.isAutoEnrollmentEnabled === shouldAutoEnroll &&
      override?.endsAt === selectedSession?.endsAt &&
      override?.startsAt === selectedSession?.startsAt &&
      override?.name === selectedSession?.name &&
      override?.isSelectedToRequireAllItemCompletion === shouldRequireItemCompletion
    );
  };

  const getIsSubmitDisabled = (): boolean => {
    if (dateError) {
      return true;
    } else if (override) {
      return getIsSubmitDisabledWithOverride();
    } else {
      return (
        !hasUpdatedAvailability() &&
        !hasNewSession() &&
        !hasUpdatedName() &&
        !hasUpdatedCreditStatus() &&
        !hasUpdatedAutoEnroll() &&
        !hasUpdatedItemCompletionStatus()
      );
    }
  };

  const handleRequestClose = () => {
    if (step === 0) {
      onRequestClose();
    } else {
      setStep(0);
    }
  };

  const getDrawerHeaderText = () => {
    const enableCourseProfiles = EnterpriseExperiments.get('enableCourseProfiles');
    if (onSelectSession && !isIncludedInContentCuration) {
      // eslint-disable-next-line no-nested-ternary
      return isS12n
        ? isProfessionalCertificate
          ? _t('Add Professional Certificate')
          : _t('Add  Specialization')
        : _t('Add course');
    } else if (isS12n) {
      if (enableCourseProfiles) {
        return isProfessionalCertificate
          ? _t('Edit Professional Certificate settings')
          : _t('Edit Specialization settings');
      }
      return isProfessionalCertificate
        ? _t('Edit Professional Certificate availability')
        : _t('Edit Specialization availability');
    } else {
      return enableCourseProfiles ? _t('Edit course settings') : _t('Edit course availability');
    }
  };

  const getBackButtonText = (isSelectionDisabled?: boolean) => {
    if (isSelectionDisabled) {
      return _t('OK');
    }
    return step === 2 ? _t('Discard Changes') : _t('Back');
  };

  if (isLoading) {
    return null;
  }

  const partner = course?.partners?.[0] || s12n?.latestSpecialization?.partners?.[0];
  const createSessionEligibility = productConfig?.productConfigurationType?.definition?.createSessionEligibility;
  if (!createSessionEligibility) {
    return null;
  }

  const selectableSessionsWithOptionType = selectableSessions.map((s) => s.optionType);
  const isSelectionDisabled = Boolean(
    productConfig.productConfigurationType.definition.s12nIdsConfiguredInProgram?.length
  );

  return useAsDialogContent ? (
    <PrivateSessionDrawerContext.Provider value={{ isS12n, isSelectionDisabled, isProfessionalCertificate }}>
      <PrivateSessionEditDrawerStep1
        selectableSessions={selectableSessionsWithOptionType}
        sessions={sessions}
        createSessionEligibility={createSessionEligibility}
        selectedSessionOption={selectedSessionOption}
        selectedSession={selectedSession}
        dateError={dateError}
        isSelectedForCredit={Boolean(shouldOfferProductForCredit)}
        isSelectedToRequireAllItemCompletion={Boolean(shouldRequireItemCompletion)}
        isAutoEnrolling={Boolean(shouldAutoEnroll)}
        hasUpdatedAvailability={
          hasUpdatedName() || (hasNewSession() && selectedSessionOption?.typeName !== 'flexibleOption')
        }
        hasUpdatedCreditStatus={hasUpdatedCreditStatus()}
        hasUpdatedItemCompletionStatus={hasUpdatedItemCompletionStatus()}
        hasUpdatedAutoEnroll={hasUpdatedAutoEnroll()}
        creationMode={Boolean(onSelectSession)}
        onChangeSessionOption={handleChangeSession}
        onChangeDate={handleChangeCustomDate}
        onChangeName={handleChangeCustomName}
        onChangeCourseCredit={handleOfferCourseForCredit}
        onChangeItemCompletion={handleToRequireAllContent}
        onChangeAutoEnroll={handleChangeAutoEnroll}
        productId={productId}
        setDateError={setDateError}
      />
    </PrivateSessionDrawerContext.Provider>
  ) : (
    <PrivateSessionDrawerContext.Provider value={{ isS12n, isSelectionDisabled, isProfessionalCertificate }}>
      <Drawer open={isOpen} width={720} anchor="right" onRequestClose={handleRequestClose}>
        <div css={drawerContainerStyle}>
          <Heading as="h2">{getDrawerHeaderText()}</Heading>
          <Grid container direction="column" css={productDetailsStyle}>
            <Grid container direction="row">
              <Imgix
                src={course?.promoPhotoUrl || s12n?.latestSpecialization.cardImageUrl || ''}
                key={partner?.id}
                alt={_t('Product logo')}
                maxHeight={LOGO_SIZE}
                maxWidth={LOGO_SIZE}
              />
              <div>
                <Grid container direction="column">
                  <Typography2 component="p">{course?.name || s12n?.latestSpecialization.name}</Typography2>
                  <Typography2 component="p" color="supportText" variant="bodySecondary">
                    {partner?.name}
                  </Typography2>
                </Grid>
                <div>
                  <ProductTypeBadge
                    className="m-r-1s"
                    productType={
                      isS12n
                        ? isProfessionalCertificate
                          ? _t('Professional Certificate')
                          : _t('specialization')
                        : _t('course')
                    }
                    data-testid={`private-session-edit-drawer-${isS12n ? 's12n' : 'course'}-badge`}
                  />
                  {metaData && 'isExclusive' in metaData && metaData?.isExclusive && (
                    <ExclusiveBadge className="m-r-1s" />
                  )}
                  {metaData?.isSelectedForCredit &&
                    (useCampusWording ? <CreditBadge className="m-r-1s" /> : <ItemLockedBadge className="m-r-1s" />)}
                  {metaData?.isRecommendedForCredit && !metaData?.isSelectedForCredit && (
                    <RecommendedForCreditBadge
                      className="m-r-1s"
                      data-testid="private-session-edit-drawer-recommended-for-credit-badge"
                    />
                  )}
                  {isS12n && (
                    <Typography2 component="p" variant="bodySecondary" color="supportText">
                      <FormattedMessage
                        message={_t('{numOfCourses} {numOfCourses, plural, =1 {course} other {courses}}')}
                        numOfCourses={s12n?.latestSpecialization?.courseCount ?? 0}
                      />
                      {lenOfS12n && (
                        <FormattedMessage
                          message={_t(
                            ' • Approx. {numOfMonths} {numOfMonths, plural, =1 {month} other {months}} to complete'
                          )}
                          numOfMonths={lenOfS12n}
                        />
                      )}
                    </Typography2>
                  )}
                </div>
              </div>
            </Grid>
            {!isS12n && isSelectionDisabled && (
              <>
                <Spacer size={32} />
                <DrawerWarning
                  theme="blue"
                  data-testid="private-session-edit-drawer-course-configured-by-s12n-warning"
                  title={_t('This course has limited configuration options')}
                  text={_t(
                    'This course was configured as part of a Specialization or Professional Certificate. Configurations done to a Specialization’s availability and enrollment settings will override any configurations done to courses included in it. '
                  )}
                />
              </>
            )}
          </Grid>

          {step === 0 && (
            <>
              <PrivateSessionEditDrawerStep1
                selectableSessions={selectableSessionsWithOptionType}
                sessions={sessions}
                createSessionEligibility={createSessionEligibility}
                selectedSessionOption={selectedSessionOption}
                selectedSession={selectedSession}
                dateError={dateError}
                isSelectedForCredit={Boolean(shouldOfferProductForCredit)}
                isSelectedToRequireAllItemCompletion={Boolean(shouldRequireItemCompletion)}
                isAutoEnrolling={Boolean(shouldAutoEnroll)}
                hasUpdatedAvailability={
                  hasUpdatedName() || (hasNewSession() && selectedSessionOption?.typeName !== 'flexibleOption')
                }
                hasUpdatedCreditStatus={hasUpdatedCreditStatus()}
                hasUpdatedItemCompletionStatus={hasUpdatedItemCompletionStatus()}
                hasUpdatedAutoEnroll={hasUpdatedAutoEnroll()}
                creationMode={Boolean(onSelectSession)}
                onChangeSessionOption={handleChangeSession}
                onChangeDate={handleChangeCustomDate}
                onChangeName={handleChangeCustomName}
                onChangeCourseCredit={handleOfferCourseForCredit}
                onChangeItemCompletion={handleToRequireAllContent}
                onChangeAutoEnroll={handleChangeAutoEnroll}
                productId={productId}
                setDateError={setDateError}
                useCampusWording={useCampusWording}
              />
              {isS12n && (
                <>
                  <Spacer size={48} />
                  <DrawerWarning
                    theme="blue"
                    title={
                      isProfessionalCertificate
                        ? _t('Changes will be applied to all courses in this Professional Certificate')
                        : _t('Changes will be applied to all courses in this Specialization')
                    }
                    text={
                      isProfessionalCertificate
                        ? _t(
                            'Configurations made in this panel will affect all courses in this Professional Certificate. For individual course configuration, please go back and select each individual course. '
                          )
                        : _t(
                            'Configurations made in this panel will affect all courses in this Specialization. For individual course configuration, please go back and select each individual course. '
                          )
                    }
                  />
                </>
              )}
              <Spacer size={32} />
            </>
          )}

          {step === 1 && (
            <PrivateSessionEditDrawerStep2
              onChangeMigrateLearners={handleMigrateLearners}
              migrateLearners={migrateLearners}
            />
          )}

          {step === 2 && (
            <PrivateSessionEditDrawerStep3
              hasUpdatedAvailability={hasUpdatedAvailability() || hasNewSession()}
              isAlwaysAvailable={selectedSessionOption?.typeName === 'flexibleOption'}
              selectedSession={selectedSession}
              migrateLearners={migrateLearners}
              hasUpdatedName={
                hasUpdatedName() || (hasNewSession() && selectedSessionOption?.typeName !== 'flexibleOption')
              }
              hasUpdatedCreditStatus={hasUpdatedCreditStatus()}
              hasUpdatedItemCompletionStatus={hasUpdatedItemCompletionStatus()}
              hasUpdatedAutoEnroll={hasUpdatedAutoEnroll()}
              isSelectedForCredit={shouldOfferProductForCredit}
              isSelectedToRequireAllItemCompletion={shouldRequireItemCompletion}
              autoEnroll={Boolean(shouldAutoEnroll)}
              showDefaults={Boolean(onSelectSession)}
              hasConflictingS12nConfig={Boolean(
                isS12n && productConfig?.productConfigurationType?.definition?.s12nIdsConfiguredInProgram?.length
              )}
            />
          )}
          <div css={navButtonGroupStyle}>
            <Button
              data-testid="close-session-drawer"
              variant={isSelectionDisabled ? 'primary' : 'secondary'}
              onClick={handleRequestClose}
            >
              {getBackButtonText(isSelectionDisabled)}
            </Button>
            {(!getIsSubmitDisabled() || onSelectSession) && (
              <TrackedButton
                trackingName="enterprise_admin_drawer_confirm_btn"
                trackingData={{
                  userId,
                  programId,
                  orgId,
                  productId,
                }}
                data-testid="submit-session-drawer"
                className="m-l-1"
                variant="primary"
                // if the caller wants the session back as opposed to persisted, the drawer flow continues even if changes have not been made.
                disabled={
                  saving ||
                  (getIsSubmitDisabled() && (!onSelectSession || isIncludedInContentCuration || Boolean(override)))
                }
                onClick={handleNextStep}
              >
                {step === 2 ? _t('Apply Changes') : _t('Continue')}
              </TrackedButton>
            )}
          </div>
        </div>
      </Drawer>
    </PrivateSessionDrawerContext.Provider>
  );
};

export default PrivateSessionEditDrawerContent;
